'use client'

import { ArticleAuthorBio } from '@/components/common/Article'
import { ArticlePreview } from '@/components/common/ArticlePreview'
import { ButtonRadioGroup } from '@/components/common/Button'
import { ErrorBlock } from '@/components/common/ErrorBlock'
import { NoResults } from '@/components/common/NoResults'
import { SectionLabel } from '@/components/common/SectionLabel'
import { usePage } from '@/components/providers/PageProvider'
import { type WPAuthorType } from '@/defs/wordpress'
import { useLinkInternal } from '@/hooks/useLinkInternal'
import { usePagination } from '@/hooks/usePagination'

import styles from './ArticleList.module.scss'
import { ArticleListSearch } from './ArticleListSearch/ArticleListSearch'

type Props = {
  author?: WPAuthorType
  variant: 'author' | 'category' | 'search' | 'tag' | 'all'
}

export const ArticleList = ({ author, variant }: Props) => {
  const { buildRelative } = useLinkInternal()
  const { page } = usePage()
  const articleList = page?.articleList
  const route = page?.route
  const pageNumber = route?.pageNumber || 1
  const paginationOptions = usePagination({ pageNumber, totalPages: articleList?.pageCount })

  if (!articleList) return <ErrorBlock />

  const options = paginationOptions.map((o) => ({
    ...o,
    linkInternalProps: { href: buildRelative({ pageNumber: o.value }) },
  }))

  const title = page.metadata?.title || 'Articles'
  const label = page.metadata?.label || 'Articles'

  const hasPosts = articleList.posts.length > 0
  const showNoResults = !hasPosts && (variant !== 'search' || !!articleList.search)

  const showBigSearchbar = variant === 'search' && !hasPosts

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.top}>
          <SectionLabel label={label} />
          {!showBigSearchbar && <ArticleListSearch />}
        </div>
        <div className={styles.heading}>
          <h1 className={styles.title}>{title}</h1>
          {pageNumber > 1 && <p className={styles.pageNumber}>Page {pageNumber}</p>}
        </div>
        {author && <ArticleAuthorBio author={author} hideName linkDisabled />}
        {showBigSearchbar && <ArticleListSearch size='lg' />}
        {showNoResults && <NoResults message='No articles found!' size='lg' />}
        {hasPosts && (
          <ul className={styles.list}>
            {articleList.posts.map((article, i) => (
              <li key={i}>
                <ArticlePreview
                  article={article}
                  authorNoLink={variant === 'author'}
                  hideCategory={variant === 'category'}
                  isAboveFold={i === 0}
                  layout='vertical'
                  layoutMd='horizontal'
                  size='lg'
                />
              </li>
            ))}
          </ul>
        )}
        {articleList.pageCount > 1 && (
          <div className={styles.controls}>
            <ButtonRadioGroup as='LinkRoute' options={options} width='maxcontent' />
          </div>
        )}
      </div>
    </div>
  )
}
